import { useEffect, useRef, useCallback } from 'react';

/**
 * BroadcastChannel hook for cross-tab communication
 *
 * Enables real-time coordination between browser tabs viewing the same scenario.
 * When one tab saves, it broadcasts the update to other tabs.
 *
 * @param {string} channelName - Unique channel name (e.g., `scenario-${scenarioId}`)
 * @param {Function} onMessage - Callback when message received from another tab
 * @returns {Object} { broadcast } - Function to send messages to other tabs
 */
export function useBroadcastChannel(channelName, onMessage) {
  const channelRef = useRef(null);

  // Initialize channel
  useEffect(() => {
    if (!channelName) return;

    // Create channel
    channelRef.current = new BroadcastChannel(channelName);

    // Listen for messages from other tabs
    const handleMessage = (event) => {
      console.warn('[DEBUG] BroadcastChannel message received:', { channel: channelName, data: event.data });
      if (onMessage) {
        onMessage(event.data);
      }
    };

    channelRef.current.addEventListener('message', handleMessage);

    // Cleanup on unmount or channel name change
    return () => {
      if (channelRef.current) {
        channelRef.current.removeEventListener('message', handleMessage);
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [channelName, onMessage]);

  /**
   * Broadcast a message to other tabs
   * @param {Object} message - Message to send
   */
  const broadcast = useCallback((message) => {
    if (channelRef.current) {
      channelRef.current.postMessage(message);
    }
  }, []);

  return { broadcast };
}
